import PBService, {PBServiceProps} from "@irati/utils/services"
import client from "@irati/utils/services/apollo"
import AuthStream from "@irati/auth/streams/auth"

import API from "@irati/utils/services/API"
import {getBrazeApiKey, sdkEndpoint} from "../utils/env"
import {SimpleBrazeContentCard} from "../hooks/ContentCard"
import {
  changeUser,
  getCachedContentCards,
  initialize,
  setLogger,
  logCardClick,
  logCardImpressions,
  openSession,
  requestContentCardsRefresh,
  requestImmediateDataFlush,
  subscribeToSdkAuthenticationFailures,
  setSdkAuthenticationSignature
} from "./sdk"
import brazeToken, {brazeTokenKey} from "./mutations/brazeToken"
import brazeLogger, {BrazeLogger} from "./brazeLogger"

class BrazeService extends PBService {
  observer: ZenObservable.Subscription | null = null

  constructor(props: PBServiceProps, private logger: BrazeLogger) {
    super(props)

    setLogger(this.logger.log)

    initialize(getBrazeApiKey(), {
      baseUrl: sdkEndpoint,
      doNotLoadFontAwesome: true,
      enableLogging: true,
      enableSdkAuthentication: true
    })

    this.observer = AuthStream.subscribe(
      async (auth) => {
        if (auth.user && API.hasTokens()) {
          await this.setUserId(auth.user.uuid!)
          openSession()
        }
      },
      () => {
        /* no-op */
      },
      () => {
        /* no-op */
      }
    )
  }

  async setUserId(userId: string) {
    const jwtToken = await this.getBrazeJWT()
    changeUser(userId, jwtToken)

    subscribeToSdkAuthenticationFailures(async () => {
      const newJWT = await this.getBrazeJWT()
      setSdkAuthenticationSignature(newJWT)
    })
  }

  getContentCards(): SimpleBrazeContentCard[] {
    requestContentCardsRefresh()
    return getCachedContentCards().cards as unknown as SimpleBrazeContentCard[]
  }

  markCardAsClicked(id: string) {
    const cards = getCachedContentCards().cards
    const card = cards.find((c) => c.id === id)
    if (card) {
      // HACK needed to ensure these are set so braze marks them as seen
      card.url = card.extras?.component_id ? "IGNORE" : card.url
      logCardClick(card, true)
      requestImmediateDataFlush()
    }
  }

  markCardsAsSeen(ids: string[]) {
    const cards = getCachedContentCards()
      .cards.filter((card) => ids.includes(card.id ?? ""))
      .map((card) => {
        // HACK needed to ensure these are set so braze marks them as seen
        card.url = card.extras?.component_id ? "IGNORE" : card.url
        return card
      })
    if (cards.length) {
      logCardImpressions(cards, true)
      requestImmediateDataFlush()
    }
  }

  async getBrazeJWT(): Promise<string> {
    const {data} = await client.mutate({
      mutation: brazeToken
    })
    return data[brazeTokenKey]
  }
}

const brazeService = new BrazeService({errorHandlers: []}, brazeLogger)

export default brazeService
